import translate from '@translate';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import Grid, { GridItem } from 'src/components/Grid';
import Select, { OptionType, SelectValueType } from 'src/components/Select';
import TextField from 'src/components/TextField';
import Button from 'src/components/Button';
import { ProductInsertCountryType, ProductType } from 'src/types/product';
import FileUpload, { CustomFileActions, FilesPropType } from 'src/components/FileUpload';
import EntityFilesTable from 'src/components/FileUpload/EntityFilesTable';
import { FileType } from 'src/types/file';
import { countryOptionsCodes } from 'src/utils/countryOptions';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import useElementSize from 'src/utils/useElementSize';
import productTypesWithoutVirtual from 'src/utils/productTypesWithoutVirtual';
import { patternSku } from 'src/utils/constatns';
import Tooltip from '../../components/Tooltip';
import { InfoIcon } from '../../components/SkladonIcon/Icons';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import { sizes } from 'src/themes/default';
import FormBlock from '../../components/FormBlock';
import colorOptionFce from '../../utils/colorOptionFce';
import { FormErrorsType, OnChangeType } from '../../types/global';
import { EditKeysType } from './constants';

export interface DetailPropsType {
    onChange: OnChangeType<EditKeysType>;
    onChangeMultiple: (updates: Partial<ProductType>) => void;
    editData: ProductType;
    editErrors: FormErrorsType;
    newProduct: boolean;
    disableEdit: boolean;
    permissionGlobalProduct: boolean;
    permissionEditDimensionsWeight: boolean;
    permissionQuantityConversion: boolean;
    permissionExpMethodEdit: boolean;
    productTypeCodes: ConstantsCodeType[];
    boolNumberCodes: ConstantsCodeType[];
    activeNumberCodes: ConstantsCodeType[];
    scanImeiSnCodes: ConstantsCodeType[];
    productInsetTypeCodes: ConstantsCodeType[];
    expeditionMethodCodes: ConstantsCodeType[];
    currencyCodes: ConstantsCodeType[];
    insertByOrderTypeCodes: ConstantsCodeType[];
    batteryTypeCodes: ConstantsCodeType[];
    onGoToEansTab: () => void;
    skladonUser?: number;
}


const InfoIconStyled = styled('span')({
    marginLeft: '10px',

    '& svg': {
        width: '14px',
        height: '14px',
        marginRight: '10px'
    }
});

/** Outlined TextField box: body line-height + input_PD (12.5+12.5) + 1px borders. */
const outlinedInputBoxHeight = `calc(${sizes.body_LH} + 25px + 2px)`;

const AttachmentsBlock = styled('div')(({ theme }) => ({
    paddingTop: sizes.body_PD,

    '& .MuiTextField-root label': {
        display: 'none',
    },

    [theme.breakpoints.up('sm')]: {
        '& .MuiList-root .MuiListItemText-root .MuiTypography-body1': {
            maxWidth: 'calc(100% - 310px)',
        },
    },
}));

const Detail: FC<DetailPropsType> = (props) => {
    const fileRef = useRef(null);
    const dimensions = useElementSize(fileRef);

    const [insertCountries, setInsertCountries] = useState<string[]>([]);

    useEffect(() => {
        if (props.editData?.PRODUCT_INSERT_COUNTRIES) {
            setInsertCountries(
                props.editData.PRODUCT_INSERT_COUNTRIES.map((item: ProductInsertCountryType): any => item.COUNTRY)
            );
        }
    }, [props.editData?.PRODUCT_INSERT_COUNTRIES]);

    const onChangePhoto = (value: FilesPropType) => {
        props.onChange('PHOTO_HASH', value[0]?.data || null, false);
    };

    const productAttachmentTypeOptions = useMemo(
        () => [
            { value: 'instruction', label: translate('file.type.instruction') },
            { value: 'label', label: translate('file.type.label') },
            { value: 'other', label: translate('file.type.other') },
        ],
        []
    );

    const handleAttachmentType = (value: unknown, error: boolean, inputFile: FileType, inputName: string) => {
        const list = props.editData.FILES || [];
        const next = list.map((f) =>
            f === inputFile || (f.id != null && inputFile.id != null && f.id === inputFile.id)
                ? { ...f, type: String(value) }
                : f
        );
        props.onChange('FILES', next, error, inputName);
    };

    const attachmentCustomActions: CustomFileActions = (inputFile) => {
        const inputName = `product-file-type-${inputFile.fileName}`;
        return (
            <Select
                options={productAttachmentTypeOptions}
                value={inputFile.type || 'other'}
                error={props.editErrors[inputName]}
                onChange={(value, error) => handleAttachmentType(value, error, inputFile, inputName)}
                name={inputName}
                required
                fullWidth
                disabled={props.disableEdit}
            />
        );
    };

    const onChangeInsertCountires = (value: SelectValueType | SelectValueType[]) => {
        let newInsertCountries: ProductInsertCountryType[] = [];
        if (Array.isArray(value)) {
            newInsertCountries = value.map(
                (country: SelectValueType): ProductInsertCountryType => ({
                    COUNTRY: String(country),
                })
            );
        }
        props.onChange('PRODUCT_INSERT_COUNTRIES', newInsertCountries, false);
    };

    return (
        <>
            <FormBlock>
                <Grid>
                    <GridItem cols={4}>
                <div style={{ width: '100%', height: '100%' }} ref={fileRef}>
                    <FileUpload
                        files={props.editData.PHOTO_HASH ? [{ data: props.editData.PHOTO_HASH }] : undefined}
                        onChange={onChangePhoto}
                        accept="image/jpeg,image/jpg,image/png"
                        image
                        height={dimensions.width}
                        disabled={props.disableEdit}
                    />
                </div>
            </GridItem>
            <GridItem cols={4}>
                <Grid>
                    <GridItem cols={props.newProduct ? 12 : 6}>
                        <Select
                            options={props.boolNumberCodes}
                            value={props.editData.ACTIVE}
                            error={props.editErrors.ACTIVE}
                            onChange={(value, error) => props.onChange('ACTIVE', value, error)}
                            name="ACTIVE"
                            label={translate('productDetail.active')}
                            required
                            fullWidth
                            disabled={props.disableEdit}
                            optionStyleFce={colorOptionFce}
                        />
                    </GridItem>
                    {!props.newProduct && (
                        <GridItem cols={6}>
                            <Select
                                options={props.boolNumberCodes}
                                value={props.editData.IS_DELETED}
                                error={props.editErrors.IS_DELETED}
                                onChange={(value, error) => props.onChange('IS_DELETED', value, error)}
                                name="IS_DELETED"
                                label={translate('productDetail.isDeleted')}
                                fullWidth
                                disabled={props.disableEdit}
                                optionStyleFce={colorOptionFce}
                            />
                        </GridItem>
                    )}
                </Grid>
                <Select
                    options={props.productTypeCodes}
                    editableOptions={productTypesWithoutVirtual(props.productTypeCodes)}
                    value={props.editData.PRODUCT_TYPE}
                    error={props.editErrors.PRODUCT_TYPE}
                    onChange={(value, error) => props.onChange('PRODUCT_TYPE', value, error)}
                    name="PRODUCT_TYPE"
                    label={translate('productDetail.productType')}
                    required
                    fullWidth
                    disabled={props.disableEdit}
                />
                <TextField
                    value={props.editData.SKU}
                    error={props.editErrors.SKU}
                    onChange={(value, error) => props.onChange('SKU', value, error)}
                    name="SKU"
                    label={<>
                        {translate('productDetail.SKU')}
                        <Tooltip title={translate('productDetail.SKU.tooltip')}>
                            <InfoIconStyled>
                                <InfoIcon />
                            </InfoIconStyled>
                        </Tooltip>
                    </>}
                    fullWidth
                    required
                    disabled={!props.newProduct || props.disableEdit}
                    inputProps={props.newProduct ? { pattern: patternSku } : undefined}
                    errorHelperText={translate('productDetail.incorretSkuText')}
                />
                <TextField
                    value={props.editData.NAME}
                    error={props.editErrors.NAME}
                    onChange={(value, error) => props.onChange('NAME', value, error)}
                    name="NAME"
                    label={translate('productDetail.name')}
                    fullWidth
                    required
                    disabled={props.disableEdit}
                />
                <TextField
                    value={props.editData.DESCRIPTION}
                    error={props.editErrors.DESCRIPTION}
                    onChange={(value, error) => props.onChange('DESCRIPTION', value, error)}
                    name="DESCRIPTION"
                    label={translate('productDetail.description')}
                    fullWidth
                    disabled={props.disableEdit}
                />
                <TextField
                    value={props.editData.HS_CODE}
                    error={props.editErrors.HS_CODE}
                    onChange={(value, error) => props.onChange('HS_CODE', value, error)}
                    name="HS_CODE"
                    label={translate('productDetail.hsCode')}
                    fullWidth
                    disabled={props.disableEdit}
                />
                <Select
                    options={props.batteryTypeCodes}
                    value={props.editData.BATTERY_TYPE || 'no'}
                    error={props.editErrors.BATTERY_TYPE}
                    onChange={(value, error) => props.onChange('BATTERY_TYPE', value || 'no', error)}
                    name="BATTERY_TYPE"
                    label={translate('productDetail.batteryType')}
                    fullWidth
                    disabled={props.disableEdit}
                    optionStyleFce={colorOptionFce}
                />
                <Select
                    options={props.productInsetTypeCodes}
                    value={props.editData.INSERT_TYPE || null}
                    error={props.editErrors.INSERT_TYPE}
                    onChange={(value, error) => props.onChange('INSERT_TYPE', value || null, error)}
                    name="INSERT_TYPE"
                    label={translate('productDetail.inserType')}
                    fullWidth
                    disabled={props.disableEdit}
                    emptyOption
                    emptyOptionValue={null}
                />
                {props.editData.INSERT_TYPE && (
                    <Select
                        options={props.insertByOrderTypeCodes}
                        value={props.editData.INSERT_BY_ORDER_TYPE || 'all'}
                        error={props.editErrors.INSERT_BY_ORDER_TYPE}
                        onChange={(value, error) => props.onChange('INSERT_BY_ORDER_TYPE', value || 'all', error)}
                        name="INSERT_BY_ORDER_TYPE"
                        label={translate('productDetail.insertByOrderType')}
                        fullWidth
                        disabled={props.disableEdit}
                        required
                    />
                )}
                {props.editData.INSERT_TYPE === 'everyOrder' && (
                    <Grid>
                        <GridItem cols={6}>
                            <TextField
                                value={props.editData.INSERT_PRICE_FROM}
                                error={props.editErrors.INSERT_PRICE_FROM}
                                onChange={(value, error) => props.onChange('INSERT_PRICE_FROM', value || null, error)}
                                name="INSERT_PRICE_FROM"
                                label={translate('productDetail.insertPriceFrom')}
                                fullWidth
                                type="numberLocale"
                                disabled={props.disableEdit}
                            />
                        </GridItem>
                        <GridItem cols={6}>
                            <TextField
                                value={props.editData.INSERT_PRICE_TO}
                                error={props.editErrors.INSERT_PRICE_TO}
                                onChange={(value, error) => props.onChange('INSERT_PRICE_TO', value || null, error)}
                                name="INSERT_PRICE_TO"
                                label={translate('productDetail.insertPriceTo')}
                                fullWidth
                                type="numberLocale"
                                disabled={props.disableEdit}
                            />
                        </GridItem>
                    </Grid>
                )}
                {props.editData.INSERT_TYPE && (
                    <Select
                        options={countryOptionsCodes}
                        value={insertCountries}
                        error={props.editErrors.PRODUCT_INSERT_COUNTRIES}
                        onChange={onChangeInsertCountires}
                        name="PRODUCT_INSERT_COUNTRIES"
                        label={translate('productDetail.productInsertToCountries')}
                        fullWidth
                        multiple
                        limitTags={2}
                        disabled={props.disableEdit}
                    />
                )}
                <Grid>
                    <GridItem cols={6}>
                        <Select
                            options={props.boolNumberCodes}
                            value={props.editData.AGECHECK}
                            error={props.editErrors.AGECHECK}
                            onChange={(value, error) => props.onChange('AGECHECK', value || '', error)}
                            name="AGECHECK"
                            label={translate('productDetail.agecheck')}
                            fullWidth
                            disabled={props.disableEdit}
                            optionStyleFce={colorOptionFce}
                        />
                    </GridItem>
                    {props.permissionQuantityConversion && (
                        <GridItem cols={6}>
                            <Select
                                options={props.boolNumberCodes}
                                value={props.editData.QUANTITY_CONVERSION}
                                error={props.editErrors.QUANTITY_CONVERSION}
                                onChange={(value, error) => props.onChange('QUANTITY_CONVERSION', value || '', error)}
                                name="QUANTITY_CONVERSION"
                                label={translate('productDetail.quantityConversion')}
                                fullWidth
                                disabled={props.disableEdit}
                                optionStyleFce={colorOptionFce}
                            />
                        </GridItem>
                    )}
                </Grid>
                <Grid>
                    {props.permissionGlobalProduct && (
                        <GridItem cols={6}>
                            <Select
                                options={props.boolNumberCodes}
                                value={props.editData.GLOBAL_PRODUCT}
                                error={props.editErrors.GLOBAL_PRODUCT}
                                onChange={(value, error) => props.onChange('GLOBAL_PRODUCT', value || '', error)}
                                name="GLOBAL_PRODUCT"
                                label={translate('productDetail.globalProduct')}
                                fullWidth
                                disabled={props.disableEdit}
                                optionStyleFce={colorOptionFce}
                            />
                        </GridItem>
                    )}
                    <GridItem cols={6}>
                        <Select
                            options={props.scanImeiSnCodes}
                            value={props.editData.SCAN_IMEI_SN_TYPE}
                            error={props.editErrors.SCAN_IMEI_SN_TYPE}
                            onChange={(value, error) => {
                                const code = value !== undefined && value !== null && value !== '' ? String(value) : 'none';
                                if (String(code).startsWith('imei')) {
                                    props.onChangeMultiple({
                                        SCAN_IMEI_SN_TYPE: code,
                                        SN_REGEX: '^[0-9]{14,16}$'
                                    });
                                } else {
                                    props.onChange('SCAN_IMEI_SN_TYPE', code, error);
                                }
                            }}
                            name="SCAN_IMEI_SN_TYPE"
                            label={translate('productDetail.scanSerialNumber')}
                            fullWidth
                            disabled={props.disableEdit}
                            optionStyleFce={colorOptionFce}
                        />
                    </GridItem>
                    <GridItem cols={12}>
                        <TextField
                            value={props.editData.SN_REGEX}
                            error={props.editErrors.SN_REGEX}
                            onChange={(value, error) => props.onChange('SN_REGEX', value, error)}
                            name="SN_REGEX"
                            label={<>
                                {translate('productDetail.snRegex')}
                                <Tooltip title={translate('productDetail.snRegex.tooltip')}>
                                    <InfoIconStyled>
                                        <InfoIcon />
                                    </InfoIconStyled>
                                </Tooltip>
                            </>}
                            fullWidth
                            disabled={props.disableEdit || String(props.editData.SCAN_IMEI_SN_TYPE || '').startsWith('imei')}
                            multiline
                        />
                    </GridItem>
                </Grid>
            </GridItem>
            <GridItem cols={4}>
                <Select
                    options={props.expeditionMethodCodes}
                    value={props.editData.EXP_METHOD}
                    error={props.editErrors.EXP_METHOD}
                    onChange={(value, error) => props.onChange('EXP_METHOD', value, error)}
                    name="EXP_METHOD"
                    label={translate('productDetail.expMethod')}
                    fullWidth
                    required
                    disabled={props.disableEdit || (!props.newProduct && !props.permissionExpMethodEdit)}
                />
                {props.newProduct ? (
                    <TextField
                        value={props.editData.EAN || ''}
                        error={props.editErrors.EAN}
                        onChange={(value, error) => props.onChange('EAN', value, error)}
                        name="EAN"
                        label={
                            <>
                                {translate('productDetail.EAN')}
                                <Tooltip title={translate('productDetail.EAN.tooltip')}>
                                    <InfoIconStyled>
                                        <InfoIcon />
                                    </InfoIconStyled>
                                </Tooltip>
                            </>
                        }
                        fullWidth
                        disabled={props.disableEdit}
                    />
                ) : (
                    <Box sx={{ display: 'flex', width: '100%', gap: 2, alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <TextField
                                value={
                                    (props.editData.EANS || [])
                                        .filter((e) => e.active)
                                        .map((e) => e.ean)
                                        .join(', ') || props.editData.EAN || ''
                                }
                                name="EAN"
                                label={
                                    <>
                                        {translate('productDetail.EAN')}
                                        <Tooltip title={translate('productDetail.EAN.tooltip')}>
                                            <InfoIconStyled>
                                                <InfoIcon />
                                            </InfoIconStyled>
                                        </Tooltip>
                                    </>
                                }
                                fullWidth
                                disabled
                                helperText={translate('productDetail.eansTab.editOnEansTab')}
                            />
                        </Box>
                        <Button
                            variant="outlined"
                            onClick={props.onGoToEansTab}
                            tooltip={translate('productDetail.eansTab.goToEans')}
                            sx={{
                                marginTop: sizes.input_top_PD,
                                whiteSpace: 'nowrap',
                                flexShrink: 0,
                                height: outlinedInputBoxHeight,
                                minHeight: outlinedInputBoxHeight,
                                boxSizing: 'border-box',
                            }}
                            data-testid="product-go-to-eans-btn"
                        >
                            {translate('productDetail.eansTab.goToEansButton')}
                        </Button>
                    </Box>
                )}
                <TextField
                    value={props.editData.ROP}
                    error={props.editErrors.ROP}
                    onChange={(value, error) => props.onChange('ROP', value, error)}
                    name="ROP"
                    label={translate('productDetail.ROP')}
                    fullWidth
                    type="numberLocale"
                    disabled={props.disableEdit}
                />
                <TextField
                    value={props.editData.EXPIRATION_DAYS}
                    error={props.editErrors.EXPIRATION_DAYS}
                    onChange={(value, error) => props.onChange('EXPIRATION_DAYS', value, error)}
                    name="EXPIRATION_DAYS"
                    label={translate('productDetail.EXPIRATION_DAYS')}
                    fullWidth
                    type="numberLocale"
                    disabled={props.disableEdit}
                />
                <Grid>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.WIDTH}
                            error={props.editErrors.WIDTH}
                            onChange={(value, error) => props.onChange('WIDTH', value, error)}
                            name="WIDTH"
                            label={translate('productDetail.width')}
                            fullWidth
                            type="numberLocale"
                            min={0}
                            disabled={!props.permissionEditDimensionsWeight}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.HEIGHT}
                            error={props.editErrors.HEIGHT}
                            onChange={(value, error) => props.onChange('HEIGHT', value, error)}
                            name="HEIGHT"
                            label={translate('productDetail.height')}
                            fullWidth
                            type="numberLocale"
                            min={0}
                            disabled={!props.permissionEditDimensionsWeight}
                        />
                    </GridItem>
                </Grid>
                <Grid>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.LENGTH}
                            error={props.editErrors.LENGTH}
                            onChange={(value, error) => props.onChange('LENGTH', value, error)}
                            name="LENGTH"
                            label={translate('productDetail.length')}
                            fullWidth
                            type="numberLocale"
                            min={0}
                            disabled={!props.permissionEditDimensionsWeight}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.WEIGHT}
                            error={props.editErrors.WEIGHT}
                            onChange={(value, error) => props.onChange('WEIGHT', value, error)}
                            name="WEIGHT"
                            label={translate('productDetail.weight')}
                            fullWidth
                            type="numberLocale"
                            min={0}
                            disabled={!props.permissionEditDimensionsWeight}
                        />
                    </GridItem>
                </Grid>
                <Grid>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.PRICE}
                            error={props.editErrors.PRICE}
                            onChange={(value, error) => props.onChange('PRICE', value, error)}
                            name="PRICE"
                            label={translate('productDetail.price')}
                            fullWidth
                            type="numberLocale"
                            min={0}
                            disabled={props.disableEdit}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <Select
                            options={props.currencyCodes}
                            value={props.editData.UNIT_PRICE || ''}
                            error={props.editErrors.UNIT_PRICE}
                            onChange={(value, error) => props.onChange('UNIT_PRICE', value || '', error)}
                            name="UNIT_PRICE"
                            label={translate('productDetail.unitPrice')}
                            fullWidth
                            disabled={props.disableEdit}
                            required={!!props.editData.INSERT_PRICE_FROM || !!props.editData.INSERT_PRICE_TO}
                        />
                    </GridItem>
                </Grid>
                <TextField
                    value={props.editData.CUSTOM_FIELD_1}
                    error={props.editErrors.CUSTOM_FIELD_1}
                    onChange={(value, error) => props.onChange('CUSTOM_FIELD_1', value, error)}
                    name="CUSTOM_FIELD_1"
                    label={translate('productDetail.customField1')}
                    fullWidth
                    disabled={props.disableEdit}
                />
                <TextField
                    value={props.editData.CUSTOM_FIELD_2}
                    error={props.editErrors.CUSTOM_FIELD_2}
                    onChange={(value, error) => props.onChange('CUSTOM_FIELD_2', value, error)}
                    name="CUSTOM_FIELD_2"
                    label={translate('productDetail.customField2')}
                    fullWidth
                    disabled={props.disableEdit}
                />
                <TextField
                    value={props.editData.CUSTOM_FIELD_3}
                    error={props.editErrors.CUSTOM_FIELD_3}
                    onChange={(value, error) => props.onChange('CUSTOM_FIELD_3', value, error)}
                    name="CUSTOM_FIELD_3"
                    label={translate('productDetail.customField3')}
                    fullWidth
                    disabled={props.disableEdit}
                />
                <Select
                    options={countryOptionsCodes}
                    value={props.editData.ORIGIN_COUNTRY}
                    error={props.editErrors.ORIGIN_COUNTRY}
                    onChange={(value, error) => props.onChange('ORIGIN_COUNTRY', value, error)}
                    name="ORIGIN_COUNTRY"
                    label={translate('productDetail.originCountry')}
                    fullWidth
                    disabled={props.disableEdit}
                />
            </GridItem>
                </Grid>
            </FormBlock>
            <AttachmentsBlock>
                <FileUpload
                    label={translate('productDetail.attachmentsTitle')}
                    files={props.editData.FILES}
                    onChange={(value, err) =>
                        props.onChange(
                            'FILES',
                            value.map((f) => ({ ...f, type: f.type || 'other' })),
                            err
                        )
                    }
                    name="FILES"
                    error={!!props.editErrors.FILES}
                    height={130}
                    disabled={props.disableEdit}
                    multiple
                    displayFilesList={false}
                />
                <EntityFilesTable
                    files={props.editData.FILES}
                    disabled={props.disableEdit}
                    onDeleteFile={(file) => {
                        const list = props.editData.FILES || [];
                        props.onChange(
                            'FILES',
                            list.filter((f) => f !== file),
                            false
                        );
                    }}
                    skladonUser={props.skladonUser}
                    customFileActions={attachmentCustomActions}
                    testIdPrefix="product-files"
                />
            </AttachmentsBlock>
        </>
    );
};

export default Detail;
